import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useVerifyRecoveryToken } from '@/query/queries/user/useVerifyRecoveryToken';
import { useResetPassword } from '@/query/queries/user/useResetPassword';
import RecoverPasswordForm from '@/components/Forms/RecoverPasswordForm';
import Preloader from '@/components/UI/Preloader';
import Notify from '@/components/Notification';
import { APP_LINKS } from '@/helpers/links';
import './index.scss';

const RecoverPassword = ({ token }) => {
  const { t } = useTranslation('auth');
  const currentToken = useRef(null);
  const isTokenRequested = useRef(false);
  const navigate = useNavigate();
  const [isVerifying, setIsVerifying] = useState(true);
  const { mutateAsync: verifyToken } = useVerifyRecoveryToken();
  const { mutateAsync: resetPassword } = useResetPassword();

  const getToken = async () => {
    let verifyError = null;

    try {
      currentToken.current = await verifyToken({ token });
    } catch (error) {
      currentToken.current = null;
      verifyError = error;
    } finally {
      setIsVerifying(false);
    }

    if (currentToken.current) {
      return;
    }

    const status = verifyError?.response?.status;
    // No error at all means the backend answered without a token, i.e. it rejected the link
    const isLinkRejected = !verifyError || (status >= 400 && status < 500);

    if (isLinkRejected) {
      Notify.error({ text: t('recover_password.link_expired') });
    } else if (status !== 500) {
      // 500 is reported by the axios interceptor
      Notify.error({ text: t('recover_password.verify_error') });
    }

    navigate(APP_LINKS.signIn);
  };

  useEffect(() => {
    // The recovery token is single-use, so it must be verified only once
    if (isTokenRequested.current) {
      return;
    }
    isTokenRequested.current = true;
    getToken();
  }, []);

  const onSubmit = async (values, { setSubmitting }) => {
    if (!currentToken.current) {
      Notify.error({ text: t('recover_password.link_expired') });
      setSubmitting(false);
      return;
    }

    try {
      await resetPassword({
        password: values.password1,
        token: currentToken.current,
      });
      navigate(APP_LINKS.signIn);
    } catch {
      // The message is shown by the mutation's onError
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="recover-password">
      <div className="recover-password__inner">
        {isVerifying ? <Preloader /> : <RecoverPasswordForm onSubmit={onSubmit} />}
      </div>
    </div>
  );
};

export default RecoverPassword;
